(() => {
  const MANAGER_KEY = '__proUGCTestimonialSpotlightManager';
  const CORE_KEY = '__proVideoLifecycleCoreV1';
  const RUNTIME_VERSION = 1;
  const ROOT_SELECTOR = '[data-pro-ugc-testimonial-spotlight]';

  const existingManager = window[MANAGER_KEY];
  if (existingManager?.version === RUNTIME_VERSION && typeof existingManager.scan === 'function') {
    existingManager.scan(document);
    return;
  }

  if (existingManager && typeof existingManager.dispose === 'function') {
    try {
      existingManager.dispose();
    } catch (error) {}
  }

  const controllers = new WeakMap();
  const activeControllers = new Set();
  const runtimeAbortController = new AbortController();
  const runtimeSignal = runtimeAbortController.signal;
  const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  const modulo = (number, base) => ((number % base) + base) % base;

  const pauseVideo = (video, reset = false) => {
    if (!video) return;

    try {
      video.pause();
      if (reset && video.readyState > 0) video.currentTime = 0;
    } catch (error) {}
  };

  const safePlay = (video) => {
    if (!video) return Promise.resolve(false);

    try {
      const promise = video.play();
      if (promise && typeof promise.then === 'function') {
        return promise.then(() => true).catch(() => false);
      }
      return Promise.resolve(true);
    } catch (error) {
      return Promise.resolve(false);
    }
  };

  class SpotlightController {
    constructor(root) {
      this.root = root;
      this.slides = Array.from(root.querySelectorAll('[data-puts-slide]'));
      this.reviews = Array.from(root.querySelectorAll('[data-puts-review]'));
      this.dots = Array.from(root.querySelectorAll('[data-puts-dot]'));
      this.prevButton = root.querySelector('[data-puts-prev]');
      this.nextButton = root.querySelector('[data-puts-next]');
      this.viewport = root.querySelector('[data-puts-viewport]');
      this.track = root.querySelector('[data-puts-track]');
      this.abortController = new AbortController();
      this.signal = this.abortController.signal;
      this.observer = null;
      this.preloadObserver = null;
      this.bufferObserver = null;
      this.fallbackFrame = null;
      this.autoplayTimer = null;
      this.videoRetryTimer = null;
      this.activeIndex = 0;
      this.visible = false;
      this.nearViewport = false;
      this.bufferReady = false;
      this.core = window[CORE_KEY] || null;
      this.hovered = false;
      this.focused = false;
      this.editorPaused = false;
      this.destroyed = false;
      this.dragStartX = 0;
      this.dragStartY = 0;
      this.dragPointerId = null;
      this.manualVideo = null;
      this.manualVideoIntentUntil = 0;
      this.playRequests = new WeakSet();
      this.metadataPrepared = new WeakSet();
      this.adjacentOwners = [-1, 1].map((direction) => ({
        root: this.root, kind: 'testimonial-side-card', direction,
      }));
      this.adjacentVideos = [null, null];
      this.autoplayEnabled = root.dataset.autoplay === 'true';
      this.videoAutoplayEnabled = root.dataset.videoAutoplay === 'true';
      this.autoplayDelay = Math.max(Number(root.dataset.autoplayDelay) || 4500, 1500);
      this.interactionDelay = Math.max(Number(root.dataset.interactionDelay) || 7000, this.autoplayDelay);
      this.mobileQuery = window.matchMedia('(max-width: 749px)');
      this.cardGap = Math.max(Number(root.dataset.mobileSidePeek) || 8, 0);
      this.dragAxis = null;
      this.dragging = false;
      this.dragDeltaX = 0;
      this.dragStartTime = 0;
      this.coverflowFrame = null;

      if (!this.slides.length) return;

      this.prepareVideos();
      this.bindEvents();
      this.observeVisibility();
      this.update(0, false);
    }

    get activeSlide() {
      return this.slides[this.activeIndex] || null;
    }

    get activeVideo() {
      return this.activeSlide?.querySelector('video') || null;
    }

    setInteractiveState(element, active) {
      if (!element) return;
      element.setAttribute('aria-hidden', active ? 'false' : 'true');
      element.toggleAttribute('inert', !active);
    }

    prepareVideos() {
      this.slides.forEach((slide) => {
        const video = slide.querySelector('video');
        if (!video) return;

        try {
          video.muted = true;
          video.defaultMuted = true;
          video.loop = true;
          video.playsInline = true;
          video.autoplay = false;
          video.preload = 'none';
          video.setAttribute('muted', '');
          video.setAttribute('loop', '');
          video.setAttribute('playsinline', '');
          video.setAttribute('webkit-playsinline', '');
          video.setAttribute('preload', 'none');
          video.removeAttribute('autoplay');
        } catch (error) {}

        video.addEventListener(
          'pointerdown',
          () => {
            this.warmVideoMetadata(video);
            this.manualVideoIntentUntil = performance.now() + 1600;
          },
          { passive: true, signal: this.signal }
        );

        video.addEventListener(
          'play',
          () => {
            if (performance.now() <= this.manualVideoIntentUntil) {
              this.manualVideo = video;
              this.stopAutoplay();
            }
          },
          { signal: this.signal }
        );

        ['pause', 'ended'].forEach((eventName) => {
          video.addEventListener(
            eventName,
            () => {
              if (this.manualVideo === video) {
                this.manualVideo = null;
                this.scheduleAutoplay();
              }
            },
            { signal: this.signal }
          );
        });

        ['stalled', 'waiting', 'suspend'].forEach((eventName) => {
          video.addEventListener(
            eventName,
            () => {
              if (eventName !== 'suspend' && this.core?.helpers?.iosWebKit) {
                this.core.helpers.reportStall?.(video);
              }
              this.scheduleVideoRetry(220);
            },
            { signal: this.signal }
          );
        });

        ['loadeddata', 'canplay'].forEach((eventName) => {
          video.addEventListener(
            eventName,
            () => this.scheduleVideoRetry(40),
            { signal: this.signal }
          );
        });
      });
    }

    warmVideoMetadata(video) {
      if (!video || this.metadataPrepared.has(video)) return;
      this.metadataPrepared.add(video);

      const policy = this.core?.helpers?.resourcePolicy?.();
      const intent = policy?.farPreload ?? 'metadata';
      if (this.core?.helpers?.setPreload) {
        this.core.helpers.setPreload(video, intent, { load: intent !== 'none' });
        return;
      }

      try {
        video.preload = intent;
        video.setAttribute('preload', intent);
        if (intent !== 'none' && video.readyState === 0 && typeof video.load === 'function') {
          video.load();
        }
      } catch (error) {}
    }

    warmActiveNeighborhood() {
      if (!this.nearViewport && !this.visible) return;
      const total = this.slides.length;
      if (!total) return;

      this.warmVideoMetadata(this.activeVideo);
      if (total > 1) this.warmVideoMetadata(
        this.slides[modulo(this.activeIndex - 1, total)]?.querySelector('video')
      );
      if (total > 2) this.warmVideoMetadata(
        this.slides[modulo(this.activeIndex + 1, total)]?.querySelector('video')
      );
      this.syncAdjacentCandidates();
    }

    canAnimate() {
      return !document.hidden && !reducedMotionQuery.matches;
    }

    canAutoplayCarousel() {
      return (
        !this.destroyed &&
        this.autoplayEnabled &&
        this.slides.length > 1 &&
        this.visible &&
        this.canAnimate() &&
        !this.hovered &&
        !this.focused &&
        !this.editorPaused &&
        !this.manualVideo
      );
    }

    canAutoplayVideo(video) {
      return (
        !this.destroyed &&
        Boolean(video) &&
        video === this.activeVideo &&
        this.videoAutoplayEnabled &&
        (this.core?.helpers?.resourcePolicy?.().allowAutoplay ?? true) &&
        this.visible &&
        this.canAnimate()
      );
    }

    clearVideoRetry() {
      if (this.videoRetryTimer === null) return;
      window.clearTimeout(this.videoRetryTimer);
      this.videoRetryTimer = null;
    }

    scheduleVideoRetry(delay = 0) {
      this.clearVideoRetry();
      const video = this.activeVideo;
      if (!this.canAutoplayVideo(video)) return;

      this.videoRetryTimer = window.setTimeout(() => {
        this.videoRetryTimer = null;
        this.playActiveVideo();
      }, delay);
    }

    playActiveVideo() {
      const video = this.activeVideo;
      if (!this.canAutoplayVideo(video) || this.playRequests.has(video)) return;

      const playback = this.core?.playback;
      if (playback) {
        const claimed = playback.claim(this, {
          priority: playback.priorities.AUTOPLAY,
          root: this.root,
          video: this.core?.helpers?.iosWebKit ? video : null,
          pause: () => this.pauseAllVideos(false),
        });
        if (!claimed) return;
      }

      const policy = this.core?.helpers?.resourcePolicy?.();
      if (this.core?.helpers?.setPreload && policy) {
        this.core.helpers.setPreload(video, policy.visiblePreload, {
          load: policy.visiblePreload !== 'none',
        });
      } else {
        this.warmVideoMetadata(video);
      }

      this.playRequests.add(video);
      safePlay(video)
        .then((didPlay) => {
          if (!didPlay) playback?.release(this);
        })
        .finally(() => this.playRequests.delete(video));
    }

    pauseAllVideos(reset = false) {
      this.clearVideoRetry();
      this.slides.forEach((slide) => pauseVideo(slide.querySelector('video'), reset));
      this.manualVideo = null;
      this.core?.playback?.release(this);
    }

    syncVideos() {
      this.slides.forEach((slide, index) => {
        const video = slide.querySelector('video');
        if (!video) return;

        if (index !== this.activeIndex || !this.visible || !this.canAnimate()) {
          pauseVideo(video);
        }
      });

      this.warmActiveNeighborhood();
      if (this.videoAutoplayEnabled) this.scheduleVideoRetry(40);
    }

    update(nextIndex, animate = true, transitionDirection = 0) {
      if (!this.slides.length) return;

      const previousIndex = this.activeIndex;
      const total = this.slides.length;
      let wrapSlide = null;

      if (total === 3 && transitionDirection !== 0) {
        const wrapIndex = transitionDirection > 0
          ? modulo(previousIndex - 1, total)
          : modulo(previousIndex + 1, total);
        wrapSlide = this.slides[wrapIndex] || null;
        wrapSlide?.classList.add('puts-media-card--teleport');
      }

      this.activeIndex = modulo(nextIndex, total);

      this.slides.forEach((slide, index) => {
        const difference = modulo(index - this.activeIndex, total);

        slide.classList.remove(
          'is-active',
          'is-prev',
          'is-next',
          'is-after',
          'is-third',
          'is-hidden'
        );

        if (difference === 0) {
          slide.classList.add('is-active');
        } else if (total === 3 && difference === 1) {
          slide.classList.add('is-next');
        } else if (total === 3 && difference === 2) {
          slide.classList.add('is-third');
        } else if (difference === 1) {
          slide.classList.add('is-next');
        } else if (difference === 2) {
          slide.classList.add('is-after');
        } else if (difference === total - 1) {
          slide.classList.add('is-prev');
        } else {
          slide.classList.add('is-hidden');
        }

        this.setInteractiveState(slide, index === this.activeIndex);
      });

      this.reviews.forEach((review, index) => {
        const active = index === this.activeIndex;
        review.classList.toggle('is-active', active);
        this.setInteractiveState(review, active);
      });

      this.dots.forEach((dot) => {
        const active = Number(dot.dataset.putsDot) === this.activeIndex;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', active ? 'true' : 'false');
      });

      if (!animate || reducedMotionQuery.matches) {
        this.root.classList.add('puts-section--no-transition');
        window.requestAnimationFrame(() => {
          if (!this.destroyed) this.root.classList.remove('puts-section--no-transition');
        });
      }

      this.renderCoverflow(0);
      if (wrapSlide) {
        window.requestAnimationFrame(() => wrapSlide?.classList.remove('puts-media-card--teleport'));
      }
      this.manualVideo = null;
      this.syncSpeculativePreload();
      this.syncVideos();
    }

    stopAutoplay() {
      if (this.autoplayTimer === null) return;
      window.clearTimeout(this.autoplayTimer);
      this.autoplayTimer = null;
    }

    scheduleAutoplay(delay = this.autoplayDelay) {
      this.stopAutoplay();
      if (!this.canAutoplayCarousel()) return;

      this.autoplayTimer = window.setTimeout(() => {
        this.autoplayTimer = null;
        if (!this.canAutoplayCarousel()) return;
        this.update(this.activeIndex + 1, true, 1);
        this.scheduleAutoplay();
      }, delay);
    }

    move(direction) {
      this.update(this.activeIndex + direction, true, direction);
      this.scheduleAutoplay(this.interactionDelay);
    }

    isCoverflow() {
      return this.mobileQuery.matches;
    }

    getRelativeSlot(index, activeIndex = this.activeIndex, preferredSide = 1) {
      const total = this.slides.length;
      if (total <= 1) return 0;

      let difference = index - modulo(activeIndex, total);
      if (difference > total / 2) difference -= total;
      if (difference < -total / 2) difference += total;

      // One DOM card cannot occupy both side positions when there are only two cards.
      // Keep it on the interaction side so the requested swipe direction remains natural.
      if (total === 2 && difference !== 0) return preferredSide < 0 ? -1 : 1;
      return difference;
    }

    readPercentVariable(name, fallback) {
      const value = Number.parseFloat(getComputedStyle(this.root).getPropertyValue(name));
      return Number.isFinite(value) ? value : fallback;
    }

    getMorphMetrics() {
      const viewportWidth = Math.max(this.viewport?.clientWidth || 0, 1);
      const activePercent = Math.min(Math.max(this.readPercentVariable('--puts-phone-active-width', 62), 54), 70);
      const sidePercent = Math.min(Math.max(this.readPercentVariable('--puts-phone-side-width', 15), 10), 20);
      const sideHeightPercent = Math.min(Math.max(this.readPercentVariable('--puts-phone-side-height', 94), 88), 100);
      let gap = Math.max(this.cardGap, 0);
      let activeWidth = viewportWidth * activePercent / 100;
      let sideWidth = viewportWidth * sidePercent / 100;
      const maximumContentWidth = Math.max(viewportWidth, 1);
      const requestedContentWidth = activeWidth + sideWidth * 2 + gap * 2;

      if (requestedContentWidth > maximumContentWidth) {
        const availableForCards = Math.max(maximumContentWidth - gap * 2, 1);
        const requestedCardWidth = Math.max(activeWidth + sideWidth * 2, 1);
        const ratio = availableForCards / requestedCardWidth;
        activeWidth *= ratio;
        sideWidth *= ratio;
      }

      const contentWidth = activeWidth + sideWidth * 2 + gap * 2;
      const outerSpace = Math.max((viewportWidth - contentWidth) / 2, 0);
      const activeHeight = activeWidth * 1.25;
      const sideHeight = activeHeight * sideHeightPercent / 100;
      this.viewport.style.height = `${activeHeight.toFixed(2)}px`;
      const prevX = outerSpace;
      const activeX = prevX + sideWidth + gap;
      const nextX = activeX + activeWidth + gap;

      return {
        viewportWidth,
        viewportHeight: activeHeight,
        activeWidth,
        sideWidth,
        activeHeight,
        sideHeight,
        gap,
        outerSpace,
        prevX,
        activeX,
        nextX,
        travel: Math.max(viewportWidth * 0.42, activeWidth * 0.78, 150)
      };
    }

    getSlotState(slot, metrics) {
      const sideTop = (metrics.activeHeight - metrics.sideHeight) / 2;
      const hiddenOffset = metrics.sideWidth + metrics.gap;

      if (slot <= -2) {
        return {
          x: metrics.prevX - hiddenOffset,
          y: sideTop,
          width: metrics.sideWidth,
          height: metrics.sideHeight,
          opacity: 0,
          blur: 0.45,
          saturation: 0.84,
          brightness: 0.94,
          shadow: 0,
          z: 4
        };
      }

      if (slot === -1) {
        return {
          x: metrics.prevX,
          y: sideTop,
          width: metrics.sideWidth,
          height: metrics.sideHeight,
          opacity: 0.62,
          blur: 0.18,
          saturation: 0.9,
          brightness: 0.96,
          shadow: 0.015,
          z: 18
        };
      }

      if (slot === 0) {
        return {
          x: metrics.activeX,
          y: 0,
          width: metrics.activeWidth,
          height: metrics.activeHeight,
          opacity: 1,
          blur: 0,
          saturation: 1,
          brightness: 1,
          shadow: 0.11,
          z: 32
        };
      }

      if (slot === 1) {
        return {
          x: metrics.nextX,
          y: sideTop,
          width: metrics.sideWidth,
          height: metrics.sideHeight,
          opacity: 0.62,
          blur: 0.18,
          saturation: 0.9,
          brightness: 0.96,
          shadow: 0.015,
          z: 18
        };
      }

      return {
        x: metrics.nextX + hiddenOffset,
        y: sideTop,
        width: metrics.sideWidth,
        height: metrics.sideHeight,
        opacity: 0,
        blur: 0.45,
        saturation: 0.84,
        brightness: 0.94,
        shadow: 0,
        z: 4
      };
    }

    interpolateState(from, to, amount) {
      const lerp = (start, finish) => start + (finish - start) * amount;
      return {
        x: lerp(from.x, to.x),
        y: lerp(from.y, to.y),
        width: lerp(from.width, to.width),
        height: lerp(from.height, to.height),
        opacity: lerp(from.opacity, to.opacity),
        blur: lerp(from.blur, to.blur),
        saturation: lerp(from.saturation, to.saturation),
        brightness: lerp(from.brightness, to.brightness),
        shadow: lerp(from.shadow, to.shadow),
        z: Math.round(lerp(from.z, to.z))
      };
    }

    setMorphState(slide, state) {
      slide.style.setProperty('--puts-morph-x', `${state.x.toFixed(2)}px`);
      slide.style.setProperty('--puts-morph-y', `${state.y.toFixed(2)}px`);
      slide.style.setProperty('--puts-morph-width', `${state.width.toFixed(2)}px`);
      slide.style.setProperty('--puts-morph-height', `${state.height.toFixed(2)}px`);
      slide.style.setProperty('--puts-morph-opacity', state.opacity.toFixed(4));
      slide.style.setProperty('--puts-morph-blur', `${state.blur.toFixed(2)}px`);
      slide.style.setProperty('--puts-morph-saturation', state.saturation.toFixed(4));
      slide.style.setProperty('--puts-morph-brightness', state.brightness.toFixed(4));
      slide.style.setProperty('--puts-morph-shadow', state.shadow.toFixed(4));
      slide.style.setProperty('--puts-morph-z', String(state.z));
    }

    clearCoverflowStyles() {
      this.viewport?.style.removeProperty('height');
      this.slides.forEach((slide) => {
        [
          '--puts-morph-x',
          '--puts-morph-y',
          '--puts-morph-width',
          '--puts-morph-height',
          '--puts-morph-opacity',
          '--puts-morph-blur',
          '--puts-morph-saturation',
          '--puts-morph-brightness',
          '--puts-morph-shadow',
          '--puts-morph-z'
        ].forEach((property) => slide.style.removeProperty(property));
      });
    }

    renderCoverflow(deltaX = 0) {
      if (!this.isCoverflow() || !this.viewport) {
        this.clearCoverflowStyles();
        return;
      }

      const metrics = this.getMorphMetrics();
      const signedProgress = Math.max(-1, Math.min(1, deltaX / metrics.travel));
      const amount = Math.abs(signedProgress);
      const direction = signedProgress < 0 ? 1 : signedProgress > 0 ? -1 : 0;
      const preferredSide = direction === -1 ? -1 : 1;
      const targetActiveIndex = direction === 0 ? this.activeIndex : this.activeIndex + direction;

      this.slides.forEach((slide, index) => {
        const fromSlot = this.getRelativeSlot(index, this.activeIndex, preferredSide);
        let toSlot = this.getRelativeSlot(index, targetActiveIndex, preferredSide);

        // With exactly three cards, the wrapping side card exits outward instead of
        // crossing through the center during the drag. It is teleported to the
        // opposite side after the main morph completes.
        if (this.slides.length === 3 && direction > 0 && fromSlot === -1 && toSlot === 1) {
          toSlot = -2;
        } else if (this.slides.length === 3 && direction < 0 && fromSlot === 1 && toSlot === -1) {
          toSlot = 2;
        }

        const fromState = this.getSlotState(fromSlot, metrics);
        const toState = this.getSlotState(toSlot, metrics);
        const state = direction === 0 ? fromState : this.interpolateState(fromState, toState, amount);
        this.setMorphState(slide, state);
      });
    }

    scheduleCoverflowRender(deltaX = this.dragDeltaX) {
      this.dragDeltaX = deltaX;
      if (this.coverflowFrame !== null) return;

      this.coverflowFrame = window.requestAnimationFrame(() => {
        this.coverflowFrame = null;
        this.renderCoverflow(this.dragDeltaX);
      });
    }

    resetDragState() {
      this.dragAxis = null;
      this.dragging = false;
      this.dragDeltaX = 0;
      this.dragPointerId = null;
      this.root.classList.remove('puts-section--dragging');
    }

    finishSwipe(deltaX, elapsed) {
      const viewportWidth = Math.max(this.viewport?.clientWidth || 0, 1);
      const distance = Math.abs(deltaX);
      const velocity = distance / Math.max(elapsed, 1);
      const shouldAdvance =
        distance >= Math.max(44, viewportWidth * 0.14) ||
        (distance >= 24 && velocity > 0.46);

      this.resetDragState();
      if (shouldAdvance) {
        const direction = deltaX < 0 ? 1 : -1;
        this.update(this.activeIndex + direction, true, direction);
      } else {
        this.renderCoverflow(0);
      }
      this.scheduleAutoplay(this.interactionDelay);
    }

    beginDrag(clientX, clientY) {
      this.dragStartX = clientX;
      this.dragStartY = clientY;
      this.dragStartTime = performance.now();
      this.dragAxis = null;
      this.dragging = false;
      this.dragDeltaX = 0;
      this.stopAutoplay();
    }

    continueDrag(clientX, clientY, event) {
      const deltaX = clientX - this.dragStartX;
      const deltaY = clientY - this.dragStartY;

      if (!this.dragAxis && Math.max(Math.abs(deltaX), Math.abs(deltaY)) > 5) {
        this.dragAxis = Math.abs(deltaX) > Math.abs(deltaY) * 1.08 ? 'x' : 'y';
      }

      if (this.dragAxis !== 'x') return false;
      event?.preventDefault?.();
      if (!this.isCoverflow()) return false;

      this.dragging = true;
      this.root.classList.add('puts-section--dragging');
      const limit = Math.max((this.viewport?.clientWidth || 0) * 0.78, 210);
      this.scheduleCoverflowRender(Math.max(-limit, Math.min(limit, deltaX)));
      return true;
    }

    endDrag(clientX, clientY) {
      const deltaX = clientX - this.dragStartX;
      const deltaY = clientY - this.dragStartY;
      const elapsed = performance.now() - this.dragStartTime;

      if (this.isCoverflow() && this.dragging) {
        this.finishSwipe(deltaX, elapsed);
        return;
      }

      this.resetDragState();
      if (Math.abs(deltaX) > 42 && Math.abs(deltaX) > Math.abs(deltaY)) {
        const direction = deltaX < 0 ? 1 : -1;
        this.update(this.activeIndex + direction, true, direction);
      }
      this.scheduleAutoplay(this.interactionDelay);
    }

    cancelDrag() {
      this.resetDragState();
      this.renderCoverflow(0);
      this.scheduleAutoplay(this.interactionDelay);
    }

    bindPointerDrag() {
      this.viewport?.addEventListener(
        'pointerdown',
        (event) => {
          if (!event.isPrimary || (event.pointerType === 'mouse' && event.button !== 0)) return;
          if (event.target?.closest?.('video[controls]')) return;

          this.dragPointerId = event.pointerId;
          this.beginDrag(event.clientX, event.clientY);
          try {
            this.viewport.setPointerCapture(event.pointerId);
          } catch (error) {}
        },
        { signal: this.signal }
      );

      window.addEventListener(
        'pointermove',
        (event) => {
          if (event.pointerId !== this.dragPointerId) return;
          this.continueDrag(event.clientX, event.clientY, event);
        },
        { signal: this.signal }
      );

      window.addEventListener(
        'pointerup',
        (event) => {
          if (event.pointerId !== this.dragPointerId) return;
          try {
            this.viewport?.releasePointerCapture(event.pointerId);
          } catch (error) {}
          this.endDrag(event.clientX, event.clientY);
        },
        { signal: this.signal }
      );

      window.addEventListener(
        'pointercancel',
        (event) => {
          if (this.dragPointerId !== null && event.pointerId !== this.dragPointerId) return;
          if (this.dragging || this.dragAxis) this.cancelDrag();
          else this.resetDragState();
        },
        { signal: this.signal }
      );
    }

    bindTouchFallback() {
      this.viewport?.addEventListener(
        'touchstart',
        (event) => {
          const touch = event.changedTouches[0];
          this.beginDrag(touch.clientX, touch.clientY);
        },
        { passive: true, signal: this.signal }
      );

      this.viewport?.addEventListener(
        'touchmove',
        (event) => {
          const touch = event.changedTouches[0];
          this.continueDrag(touch.clientX, touch.clientY, event);
        },
        { passive: false, signal: this.signal }
      );

      this.viewport?.addEventListener(
        'touchend',
        (event) => {
          const touch = event.changedTouches[0];
          this.endDrag(touch.clientX, touch.clientY);
        },
        { passive: true, signal: this.signal }
      );

      this.viewport?.addEventListener(
        'touchcancel',
        () => this.cancelDrag(),
        { passive: true, signal: this.signal }
      );
    }

    bindEvents() {
      this.prevButton?.addEventListener('click', () => this.move(-1), { signal: this.signal });
      this.nextButton?.addEventListener('click', () => this.move(1), { signal: this.signal });

      this.dots.forEach((dot) => {
        dot.addEventListener(
          'click',
          () => {
            const index = Number(dot.dataset.putsDot);
            if (!Number.isNaN(index)) this.update(index);
            this.scheduleAutoplay(this.interactionDelay);
          },
          { signal: this.signal }
        );
      });

      this.root.addEventListener(
        'mouseenter',
        () => {
          this.hovered = true;
          this.stopAutoplay();
        },
        { signal: this.signal }
      );

      this.root.addEventListener(
        'mouseleave',
        () => {
          this.hovered = false;
          this.scheduleAutoplay();
        },
        { signal: this.signal }
      );

      this.root.addEventListener(
        'focusin',
        () => {
          this.focused = true;
          this.stopAutoplay();
        },
        { signal: this.signal }
      );

      this.root.addEventListener(
        'focusout',
        (event) => {
          if (event.relatedTarget && this.root.contains(event.relatedTarget)) return;
          this.focused = false;
          this.scheduleAutoplay();
        },
        { signal: this.signal }
      );

      this.viewport?.addEventListener('dragstart', (event) => event.preventDefault(), { signal: this.signal });

      if ('PointerEvent' in window) this.bindPointerDrag();
      else this.bindTouchFallback();

      this.slides.forEach((slide) => {
        const media = slide.querySelector('img, video');
        if (!media) return;
        ['load', 'loadedmetadata'].forEach((eventName) => {
          media.addEventListener(
            eventName,
            () => {
              this.scheduleCoverflowRender(0);
            },
            { passive: true, signal: this.signal }
          );
        });
      });

      window.addEventListener(
        'resize',
        () => {
          this.scheduleCoverflowRender(0);
        },
        { passive: true, signal: this.signal }
      );
    }

    updateFallbackVisibility() {
      this.fallbackFrame = null;
      const rect = this.root.getBoundingClientRect();
      const visible = rect.bottom > 0 && rect.top < window.innerHeight;
      this.setVisibility(visible);
    }

    scheduleFallbackVisibility() {
      if (this.fallbackFrame !== null) return;
      this.fallbackFrame = window.requestAnimationFrame(() => this.updateFallbackVisibility());
    }

    setSpeculativePreload(video, intent) {
      if (!video) return;
      if (this.core?.helpers?.setPreload) {
        this.core.helpers.setPreload(video, intent, { load: intent !== 'none' });
        return;
      }
      try {
        video.preload = intent;
        video.setAttribute('preload', intent);
        if (intent !== 'none' && video.readyState === 0 && typeof video.load === 'function') {
          video.load();
        }
      } catch (error) {}
    }

    syncSpeculativePreload() {
      const manager = this.core?.preload;
      const policy = this.core?.helpers?.resourcePolicy?.();
      const video = this.activeVideo;

      if (
        !manager ||
        !video ||
        !policy?.allowAutoplay ||
        policy.speculativePreload === 'none'
      ) {
        manager?.unregister?.(this);
        return;
      }

      manager.register(this, {
        root: this.root,
        video,
        warmMode: 'adaptive',
        isUrgent: () => this.visible || this.nearViewport || this.bufferReady,
        isEligible: () => (
          !this.destroyed &&
          !document.hidden &&
          !reducedMotionQuery.matches &&
          (this.core?.helpers?.resourcePolicy?.().speculativePreload || 'none') !== 'none'
        ),
        onGrant: () => {
          const nextPolicy = this.core?.helpers?.resourcePolicy?.();
          this.setSpeculativePreload(video, nextPolicy?.speculativePreload || 'metadata');
        },
        onRevoke: () => {
          const nextPolicy = this.core?.helpers?.resourcePolicy?.();
          if (!this.visible && video.paused) {
            this.setSpeculativePreload(video, nextPolicy?.farPreload || 'metadata');
          }
        },
      });
    }

    syncAdjacentCandidates() {
      const manager = this.core?.preload;
      const policy = this.core?.helpers?.resourcePolicy?.();
      if (!manager) return;
      const eligible = !this.destroyed && (this.nearViewport || this.visible) &&
        !document.hidden && !reducedMotionQuery.matches &&
        policy?.speculativePreload !== 'none';
      const registered = new Set(this.activeVideo ? [this.activeVideo] : []);
      const total = this.slides.length;

      [-1, 1].forEach((direction, side) => {
        const owner = this.adjacentOwners[side];
        const target = eligible && total > 1
          ? this.slides[modulo(this.activeIndex + direction, total)]?.querySelector('video') || null
          : null;
        if (!target || registered.has(target)) {
          this.adjacentVideos[side] = null;
          manager.unregister(owner);
          return;
        }
        registered.add(target);
        if (this.adjacentVideos[side] === target) return;
        this.adjacentVideos[side] = target;
        manager.register(owner, {
          root: this.root,
          video: target,
          warmMode: 'adjacent',
          isEligible: () => !this.destroyed && (this.nearViewport || this.visible) &&
            !document.hidden && !reducedMotionQuery.matches &&
            this.core.helpers.resourcePolicy().speculativePreload !== 'none',
          onGrant: () => this.setSpeculativePreload(target, 'auto'),
          onRevoke: () => {
            if (target !== this.activeVideo && target.paused) {
              this.setSpeculativePreload(target, 'metadata');
            }
          },
        });
      });
      manager.refresh();
    }

    setVisibility(visible) {
      if (this.visible === visible) return;
      this.visible = visible;
      this.core?.preload?.refresh?.();

      if (visible) {
        this.nearViewport = true;
        this.warmActiveNeighborhood();
        this.syncVideos();
        this.scheduleAutoplay();
      } else {
        this.stopAutoplay();
        this.pauseAllVideos();
      }
    }

    observeVisibility() {
      if ('IntersectionObserver' in window) {
        const mobile = window.matchMedia('(max-width: 989px)').matches;
        const profile = this.core?.helpers?.connectionProfile?.() || 'normal';
        const metadataMargin = mobile ? 1800 : 1800;
        const bufferMargin = mobile
          ? (profile === 'balanced' ? 1700 : 1500)
          : (profile === 'balanced' ? 1100 : 850);

        this.preloadObserver = new IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            this.nearViewport = true;
            this.warmActiveNeighborhood();
            this.preloadObserver?.disconnect();
            this.preloadObserver = null;
          },
          { threshold: 0, rootMargin: `${metadataMargin}px 0px` }
        );
        this.preloadObserver.observe(this.root);

        this.bufferObserver = new IntersectionObserver(
          (entries) => {
            this.bufferReady = entries.some((entry) => entry.isIntersecting);
            this.syncSpeculativePreload();
            this.core?.preload?.refresh?.();
          },
          { threshold: 0, rootMargin: `${bufferMargin}px 0px` }
        );
        this.bufferObserver.observe(this.root);

        this.observer = new IntersectionObserver(
          (entries) => {
            const entry = entries[0];
            this.setVisibility(Boolean(entry && entry.isIntersecting && entry.intersectionRatio > 0.15));
          },
          { threshold: [0, 0.15, 0.45] }
        );
        this.observer.observe(this.root);
        return;
      }

      this.nearViewport = true;
      this.bufferReady = true;
      this.warmActiveNeighborhood();
      this.syncSpeculativePreload();
      ['scroll', 'resize', 'orientationchange'].forEach((eventName) => {
        window.addEventListener(
          eventName,
          () => this.scheduleFallbackVisibility(),
          { passive: true, signal: this.signal }
        );
      });
      this.updateFallbackVisibility();
    }

    selectBlock(target) {
      const selected = target?.closest?.('[data-puts-slide], [data-puts-review]');
      if (!selected || !this.root.contains(selected)) return false;

      const index = Number(selected.dataset.putsSlide ?? selected.dataset.putsReview);
      if (Number.isNaN(index)) return false;

      this.editorPaused = true;
      this.stopAutoplay();
      this.update(index, false);
      return true;
    }

    deselectBlock() {
      this.editorPaused = false;
      this.scheduleAutoplay();
    }

    onVisibilityChange() {
      this.core?.preload?.refresh?.();
      if (document.hidden) {
        this.stopAutoplay();
        this.pauseAllVideos();
      } else if (this.visible) {
        this.syncVideos();
        this.scheduleAutoplay();
      }
    }

    onMotionChange() {
      this.core?.preload?.refresh?.();
      if (reducedMotionQuery.matches) {
        this.stopAutoplay();
        this.pauseAllVideos();
      } else if (this.visible) {
        this.syncVideos();
        this.scheduleAutoplay();
      }
    }

    onUserGesture(event) {
      const targetVideo = event?.target?.closest?.('video');
      if (targetVideo === this.activeVideo && targetVideo.controls) return;
      if (this.canAutoplayVideo(this.activeVideo)) this.scheduleVideoRetry(20);
    }

    destroy() {
      if (this.destroyed) return;
      this.destroyed = true;
      this.stopAutoplay();
      this.clearVideoRetry();
      this.preloadObserver?.disconnect();
      this.bufferObserver?.disconnect();
      this.observer?.disconnect();
      this.abortController.abort();
      if (this.fallbackFrame !== null) window.cancelAnimationFrame(this.fallbackFrame);
      if (this.coverflowFrame !== null) window.cancelAnimationFrame(this.coverflowFrame);
      this.core?.preload?.unregister?.(this);
      this.adjacentOwners.forEach((owner) => this.core?.preload?.unregister?.(owner));
      this.pauseAllVideos();
      this.clearCoverflowStyles();
      this.root.classList.remove('puts-section--no-transition', 'puts-section--dragging');
      this.slides.forEach((slide) => slide.classList.remove('puts-media-card--teleport'));
    }
  }

  const init = (root) => {
    if (!(root instanceof Element) || controllers.has(root)) return;

    const controller = new SpotlightController(root);
    if (!controller.slides.length) return;

    controllers.set(root, controller);
    activeControllers.add(controller);
  };

  const destroy = (root) => {
    const controller = controllers.get(root);
    if (!controller) return;

    controller.destroy();
    controllers.delete(root);
    activeControllers.delete(controller);
  };

  const scan = (scope = document) => {
    if (scope instanceof Element && scope.matches(ROOT_SELECTOR)) {
      init(scope);
    }
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(init);
  };

  const destroyWithin = (scope) => {
    if (!(scope instanceof Element)) return;
    if (scope.matches(ROOT_SELECTOR)) destroy(scope);
    scope.querySelectorAll(ROOT_SELECTOR).forEach(destroy);
  };

  const notify = (method, argument) => {
    activeControllers.forEach((controller) => {
      try {
        controller[method]?.(argument);
      } catch (error) {}
    });
  };

  const destroyAll = () => {
    Array.from(activeControllers).forEach((controller) => {
      try {
        destroy(controller.root);
      } catch (error) {}
    });
  };

  const handleVisibilityChange = () => notify('onVisibilityChange');
  const handleMotionChange = () => notify('onMotionChange');
  const handlePointerDown = (event) => notify('onUserGesture', event);
  const handleKeyDown = (event) => notify('onUserGesture', event);
  const handleSectionLoad = (event) => scan(event.target);
  const handleSectionUnload = (event) => destroyWithin(event.target);
  const handleBlockSelect = (event) => {
    const root = event.target?.closest?.(ROOT_SELECTOR);
    controllers.get(root)?.selectBlock(event.target);
  };
  const handleBlockDeselect = (event) => {
    const root = event.target?.closest?.(ROOT_SELECTOR);
    controllers.get(root)?.deselectBlock();
  };
  const handlePageHide = () => destroyAll();
  const handlePageShow = () => scan(document);

  document.addEventListener('visibilitychange', handleVisibilityChange, { signal: runtimeSignal });
  document.addEventListener('pointerdown', handlePointerDown, { passive: true, signal: runtimeSignal });
  document.addEventListener('keydown', handleKeyDown, { signal: runtimeSignal });
  document.addEventListener('shopify:section:load', handleSectionLoad, { signal: runtimeSignal });
  document.addEventListener('shopify:section:unload', handleSectionUnload, { signal: runtimeSignal });
  document.addEventListener('shopify:block:select', handleBlockSelect, { signal: runtimeSignal });
  document.addEventListener('shopify:block:deselect', handleBlockDeselect, { signal: runtimeSignal });
  window.addEventListener('pagehide', handlePageHide, { signal: runtimeSignal });
  window.addEventListener('pageshow', handlePageShow, { signal: runtimeSignal });

  let motionListenerMode = null;
  if (typeof reducedMotionQuery.addEventListener === 'function') {
    reducedMotionQuery.addEventListener('change', handleMotionChange);
    motionListenerMode = 'event';
  } else if (typeof reducedMotionQuery.addListener === 'function') {
    reducedMotionQuery.addListener(handleMotionChange);
    motionListenerMode = 'legacy';
  }

  let manager = null;
  const dispose = () => {
    destroyAll();
    runtimeAbortController.abort();

    if (motionListenerMode === 'event') {
      reducedMotionQuery.removeEventListener?.('change', handleMotionChange);
    } else if (motionListenerMode === 'legacy') {
      reducedMotionQuery.removeListener?.(handleMotionChange);
    }
    motionListenerMode = null;

    if (window[MANAGER_KEY] === manager) delete window[MANAGER_KEY];
  };

  manager = Object.freeze({
    version: RUNTIME_VERSION,
    scan,
    destroy,
    dispose
  });

  window[MANAGER_KEY] = manager;
  scan(document);
})();
